'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Keyboard, X, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FEATURE_FLAGS } from '@/lib/env';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getShortcuts = () => {
  // Build actions list conditionally
  const actionItems = [
    { key: "D", description: "Calculator Debug (dev mode)" },
    { key: "E", description: "Examples Page" },
  ];
  
  // Only add About Page shortcut if not hidden
  if (!FEATURE_FLAGS.hideAboutPage) {
    actionItems.push({ key: "A", description: "About Page" });
  }
  
  // Add remaining actions
  actionItems.push(
    { key: "O", description: "Start Over (reset calculations)" },
    { key: "R", description: "Reset calculator inputs (and go to Step 1)" },
    { key: "P", description: "Print calculations" },
    { key: "X", description: "Share calculation (when results available)" },
    { key: "M", description: "Toggle dark/light mode" }
  );
  
  const shortcuts = [
    {
      category: "Tab Navigation",
      description: "Navigate between calculator sections",
      items: [
        { key: "L", description: "ROI Calculator (last used tab)" },
        { key: "S", description: "Settings" },
        { key: "H", description: "Help Guide" },
      ]
    },
    {
      category: "Actions & Navigation",
      description: "Perform actions and navigate to other pages",
      items: actionItems
    },
    {
      category: "Help & Reference",
      description: "Access help and shortcuts",
      items: [
        { key: "K", description: "Show keyboard shortcuts (this modal)" },
        { key: "?", description: "Show keyboard shortcuts (this modal)" },
      ]
    }
  ];
  
  return shortcuts;
};

export function KeyboardShortcutsModal({ open, onOpenChange }: KeyboardShortcutsModalProps) {
  const shortcuts = getShortcuts();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-[98vw] max-h-[90vh] overflow-y-auto p-3 sm:p-4 md:p-6">
        <DialogHeader>
          <div className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-center gap-2">
              <Keyboard className="h-5 w-5 text-accessnow-orange" />
              <DialogTitle>Keyboard Shortcuts</DialogTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="space-y-8">
          <div className="text-center">
            <p className="text-base text-muted-foreground leading-relaxed">
              Use these keyboard shortcuts to navigate quickly throughout the application. 
              Most shortcuts work from any page.
            </p>
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              <strong>Hold Shift</strong> to see shortcut hints on relevant buttons
            </p>
          </div>

          <div className="space-y-8">
            {shortcuts.map((section) => (
              <div key={section.category} className="space-y-4">
                <div className="text-center">
                  <h3 className="font-semibold text-foreground text-xl mb-1">{section.category}</h3>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </div>
                
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                  {section.items.map((shortcut) => (
                    <div 
                      key={shortcut.key} 
                      className="flex items-center justify-between py-4 px-5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors min-h-[52px] border border-muted/50"
                    >
                      <span className="text-sm text-foreground leading-relaxed flex-1 pr-4">{shortcut.description}</span>
                      <Badge 
                        variant="outline" 
                        className="font-mono text-sm bg-background font-semibold px-3 py-1.5 min-w-[40px] text-center"
                      >
                        {shortcut.key}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                Tip: Press Esc or click outside to close this modal
              </span>
              <span>Works on: Calculator, Home, About, Examples pages</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}